import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PPTUpgradeModal from './components/PPTUpgradeModal';
import PDFUpgradeModal from './components/PDFUpgradeModal';
import Home from './pages/Home';
import LearnMore from './pages/LearnMore';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import CompleteProfile from './pages/CompleteProfile';
import AuthCallback from './pages/AuthCallback';
import PricingPage from './pages/PricingPage';
import PaymentSuccess from './pages/PaymentSuccess';
import PoliciesPage from './pages/PrivacyPolicy';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import SitemapPage from './pages/SitemapPage';
import CookieConsent from './components/CookieConsent';
import NotFound from './pages/NotFound';
import LPUFaculty from './pages/LPUFaculty';
import LPULogin from './pages/LPULogin';
import NominationsPage from './pages/NominationsPage';
import NominationsPortal from './pages/NominationsPortal';
import BecomeAFellow from './pages/BecomeAFellow';
import QuizJoinPage from './pages/QuizJoinPage';
import { InstitutionProvider } from './context/InstitutionContext';
import ScrollToHash from './components/ScrollToHash';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import RouteLoadingFallback from './components/RouteLoadingFallback';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { HelmetProvider } from 'react-helmet-async';
import GlobalPrompts from './components/GlobalPrompts';
import FloatingThemeToggle from './components/FloatingThemeToggle';
import HideOnRoutes from './components/HideOnRoutes';
import './styles/index.css';
import './styles/responsive.css'; // Responsive design system
import './styles/mobile-responsive-fixes.css'; // Mobile fixes for all devices including iPhone 16
import './styles/ModernTheme.css'; // Add Modern Theme
import './styles/theme-light.css';
import './styles/theme-dark.css';
import './styles/theme-dark-extended.css'; // Extended dark mode contrast fixes
import './styles/theme-light-overrides.css';

// Standalone public marketing page — heavy (canvas + framer-motion), so it's
// lazy rather than in the main bundle every visitor downloads.
const ChallengeTheTrust = lazy(() => import('./pages/ChallengeTheTrust'));
// LPU Freshers' 2026 landing page — same standalone/lazy pattern.
const Freshmen2026 = lazy(() => import('./pages/Freshmen2026'));
// Download page for the MARS desktop exam client. Lazy for the same reason,
// and because almost every visitor to teachgenie.ai never needs it — only
// students told to install the app for a proctored exam.
const DownloadMars = lazy(() => import('./pages/DownloadMars'));

// Protected app cluster — behind ProtectedRoute, never downloaded by a
// logged-out visitor (see ProtectedRoute.js sequencing).
const GeneratorPage = lazy(() => import('./pages/GeneratorPage'));
const StudioPage = lazy(() => import('./pages/StudioPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const FellowDashboard = lazy(() => import('./pages/FellowDashboard'));
const FellowManage = lazy(() => import('./pages/FellowManage'));
const RedemptionStore = lazy(() => import('./pages/RedemptionStore'));

// Assess-A-Thon institution cluster — reached via institution-specific
// links, not the main landing page.
const AssessathonLanding = lazy(() => import('./pages/AssessathonLanding'));
const AssessathonLogin = lazy(() => import('./pages/AssessathonLogin'));
const AssessathonSetup = lazy(() => import('./pages/AssessathonSetup'));
const AssessathonWelcome = lazy(() => import('./pages/AssessathonWelcome'));
const AssessathonSelect = lazy(() => import('./pages/AssessathonSelect'));
const AssessathonRuleBook = lazy(() => import('./pages/AssessathonRuleBook'));
const AssessathonTerms = lazy(() => import('./pages/AssessathonTerms'));
const AssessathonDashboard = lazy(() => import('./pages/AssessathonDashboard'));
const AssessathonSubmission = lazy(() => import('./pages/AssessathonSubmission'));
const AssessathonLeaderboard = lazy(() => import('./pages/AssessathonLeaderboard'));
const AssessathonResults = lazy(() => import('./pages/AssessathonResults'));
const AssessathonProfile = lazy(() => import('./pages/AssessathonProfile'));
const CertificateVerification = lazy(() => import('./pages/CertificateVerification'));

// Admin cluster — self-guarded internally (see AdminDashboard.js), not
// wrapped in ProtectedRoute.
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminSetup = lazy(() => import('./pages/admin/AdminSetup'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminInstitutions = lazy(() => import('./pages/admin/AdminInstitutions'));
const AdminInstitutionAnalytics = lazy(() => import('./pages/admin/AdminInstitutionAnalytics'));
const AdminInstitutionParticipants = lazy(() => import('./pages/admin/AdminInstitutionParticipants'));
const AdminInstitutionDetail = lazy(() => import('./pages/admin/AdminInstitutionDetail'));
const AdminSubmissions = lazy(() => import('./pages/admin/AdminSubmissions'));
const AdminAuditLog = lazy(() => import('./pages/admin/AdminAuditLog'));
const AdminAdminManagement = lazy(() => import('./pages/admin/AdminAdminManagement'));
const AdminConfigurationPage = lazy(() => import('./pages/admin/AdminConfigurationPage'));
const AdminMonitor = lazy(() => import('./pages/admin/AdminMonitor'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));

const resolveAdminPortalUrl = (pathname = '', search = '') => {
  const suffix = pathname.replace(/^\/adminportal/, '');
  const path = suffix || '/';

  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return `http://localhost:3001${path}${search}`;
  }

  return `${window.location.origin}/adminportal${path}${search}`;
};

const AdminPortalRedirect = () => {
  const location = useLocation();

  useEffect(() => {
    window.location.replace(resolveAdminPortalUrl(location.pathname, location.search));
  }, [location.pathname, location.search]);

  return null;
};

const AssessathonAdminRedirect = () => {
  const location = useLocation();
  const suffix = location.pathname.replace(/^\/assessathon\/admin/, '');

  return <Navigate to={`/admin/assessathon${suffix}${location.search}`} replace />;
};

function ModalTestHarness() {
  const params = new URLSearchParams(window.location.search);
  const which = params.get('which') || 'ppt';
  const tier = params.get('tier') || 'free';
  return (
    <div style={{ background: '#222', minHeight: '100vh' }}>
      {which === 'ppt' && <PPTUpgradeModal isOpen={true} onClose={() => {}} currentTier={tier} />}
      {which === 'pdf' && <PDFUpgradeModal isOpen={true} onClose={() => {}} currentTier={tier} />}
    </div>
  );
}

function App() {
  // Global Scroll Reveal Logic
  useEffect(() => {
    const revealCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        } else {
          // Optional: removal for "scroll-up" hiding effect
          // entry.target.classList.remove('active');
        }
      });
    };

    const revealObserver = new IntersectionObserver(revealCallback, {
      threshold: 0.05,
      rootMargin: '0px 0px -10% 0px'
    });

    const observeElements = () => {
      const elements = document.querySelectorAll('.reveal, .reveal-up, .reveal-down, .reveal-left, .reveal-right, .reveal-text, .reveal-stagger');
      elements.forEach(el => revealObserver.observe(el));
    };

    // Initial observation
    observeElements();

    // Use MutationObserver to catch dynamically added elements (like after API calls)
    const mutationObserver = new MutationObserver(() => {
      observeElements();
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      revealObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <AuthProvider>
          <ThemeProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ScrollToHash />
              <GlobalPrompts />
              <Routes>
                <Route path="/__modal_test" element={<ModalTestHarness />} />
                <Route path="/" element={<Home />} />
                <Route path="/challenge-the-trust" element={<Suspense fallback={<RouteLoadingFallback />}><ChallengeTheTrust /></Suspense>} />
                <Route path="/downloadmars" element={<Suspense fallback={<RouteLoadingFallback />}><DownloadMars /></Suspense>} />
                {/* Likely mistypings of a link students are given verbally or on
                    a slide — cheaper to redirect than to field the support call. */}
                <Route path="/download-mars" element={<Navigate to="/downloadmars" replace />} />
                <Route path="/downloadMARS" element={<Navigate to="/downloadmars" replace />} />
                <Route path="/freshmen2026" element={<Suspense fallback={<RouteLoadingFallback />}><Freshmen2026 /></Suspense>} />
                {/* old spelling, in case it was shared anywhere */}
                <Route path="/freshman2026" element={<Navigate to="/freshmen2026" replace />} />
                <Route path="/lpu" element={<LPUFaculty />} />
                <Route path="/lpu/login" element={<LPULogin initialMode="login" />} />
                <Route path="/lpu/register" element={<LPULogin initialMode="register" />} />
                <Route path="/studio" element={
                  <ProtectedRoute>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <StudioPage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                {/* DISABLED 2026-08-18 — sir's instruction: nominations should not be live on the site. Code kept in repo, just unrouted. */}
                {/* <Route path="/nominations" element={<NominationsPage />} /> */}
                {/* <Route path="/nominations/portal" element={<NominationsPortal />} /> */}
                {/* /team route removed 2026-08-20 per sir's instruction — page unrouted */}
                <Route path="/learn-more" element={<LearnMore />} />
                <Route path="/become-a-fellow" element={<BecomeAFellow />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/complete-profile" element={<CompleteProfile />} />
                <Route path="/auth-callback" element={<AuthCallback />} />

                {/* DISABLED 2026-08-18 — sir's instruction: Assess-A-Thon (public + admin) should not be live on the site. Code kept in repo, just unrouted. To re-enable, uncomment this whole block. */}
                {/*
                <Route path="/assessathon" element={<Suspense fallback={<RouteLoadingFallback />}><AssessathonLanding /></Suspense>} />
                <Route path="/assessathon/landing" element={<Suspense fallback={<RouteLoadingFallback />}><AssessathonLanding /></Suspense>} />
                <Route path="/institution/:institutionSlug" element={<InstitutionProvider><Suspense fallback={<RouteLoadingFallback />}><AssessathonLogin /></Suspense></InstitutionProvider>} />
                <Route path="/institution/:institutionSlug/login" element={<InstitutionProvider><Suspense fallback={<RouteLoadingFallback />}><AssessathonLogin /></Suspense></InstitutionProvider>} />
                <Route path="/institution/:institutionSlug/setup" element={<InstitutionProvider><Suspense fallback={<RouteLoadingFallback />}><AssessathonSetup /></Suspense></InstitutionProvider>} />
                <Route path="/institution/:institutionSlug/assessathon/setup" element={<InstitutionProvider><Suspense fallback={<RouteLoadingFallback />}><AssessathonSetup /></Suspense></InstitutionProvider>} />
                <Route path="/institution/:institutionSlug/welcome" element={<InstitutionProvider><Suspense fallback={<RouteLoadingFallback />}><AssessathonWelcome /></Suspense></InstitutionProvider>} />
                <Route path="/institution/:institutionSlug/assessathon" element={<InstitutionProvider><Suspense fallback={<RouteLoadingFallback />}><AssessathonSelect /></Suspense></InstitutionProvider>} />
                <Route path="/institution/:institutionSlug/assessathon/rulebook" element={<InstitutionProvider><Suspense fallback={<RouteLoadingFallback />}><AssessathonRuleBook /></Suspense></InstitutionProvider>} />
                <Route path="/institution/:institutionSlug/assessathon/terms" element={<InstitutionProvider><Suspense fallback={<RouteLoadingFallback />}><AssessathonTerms /></Suspense></InstitutionProvider>} />
                <Route path="/institution/:institutionSlug/assessathon/dashboard" element={<InstitutionProvider><Suspense fallback={<RouteLoadingFallback />}><AssessathonDashboard /></Suspense></InstitutionProvider>} />
                <Route path="/institution/:institutionSlug/assessathon/submission/:registrationId" element={<InstitutionProvider><Suspense fallback={<RouteLoadingFallback />}><AssessathonSubmission /></Suspense></InstitutionProvider>} />
                <Route path="/institution/:institutionSlug/assessathon/leaderboard" element={<InstitutionProvider><Suspense fallback={<RouteLoadingFallback />}><AssessathonLeaderboard /></Suspense></InstitutionProvider>} />
                <Route path="/institution/:institutionSlug/assessathon/results" element={<InstitutionProvider><Suspense fallback={<RouteLoadingFallback />}><AssessathonResults /></Suspense></InstitutionProvider>} />
                <Route path="/institution/:institutionSlug/assessathon/profile" element={<InstitutionProvider><Suspense fallback={<RouteLoadingFallback />}><AssessathonProfile /></Suspense></InstitutionProvider>} />
                <Route path="/certificate/verify/:certificateId" element={<Suspense fallback={<RouteLoadingFallback />}><CertificateVerification /></Suspense>} />
                <Route path="/assessathon/admin" element={<AssessathonAdminRedirect />} />
                <Route path="/assessathon/admin/*" element={<AssessathonAdminRedirect />} />
                <Route path="/admin/assessathon" element={<Navigate to="/admin/assessathon/login" replace />} />
                <Route path="/admin/assessathon/login" element={<Suspense fallback={<RouteLoadingFallback />}><AdminLogin /></Suspense>} />
                <Route path="/admin/assessathon/setup" element={<Suspense fallback={<RouteLoadingFallback />}><AdminSetup /></Suspense>} />
                <Route path="/admin/assessathon/dashboard" element={<Suspense fallback={<RouteLoadingFallback />}><AdminDashboard /></Suspense>} />
                <Route path="/admin/assessathon/institutions" element={<Suspense fallback={<RouteLoadingFallback />}><AdminInstitutions /></Suspense>} />
                <Route path="/admin/assessathon/institutions/:institutionId" element={<Suspense fallback={<RouteLoadingFallback />}><AdminInstitutionDetail /></Suspense>} />
                <Route path="/admin/assessathon/institutions/:institutionId/analytics" element={<Suspense fallback={<RouteLoadingFallback />}><AdminInstitutionAnalytics /></Suspense>} />
                <Route path="/admin/assessathon/institutions/:institutionId/participants" element={<Suspense fallback={<RouteLoadingFallback />}><AdminInstitutionParticipants /></Suspense>} />
                <Route path="/admin/assessathon/admins" element={<Suspense fallback={<RouteLoadingFallback />}><AdminAdminManagement /></Suspense>} />
                <Route path="/admin/assessathon/configuration" element={<Suspense fallback={<RouteLoadingFallback />}><AdminConfigurationPage /></Suspense>} />
                <Route path="/admin/assessathon/submissions" element={<Suspense fallback={<RouteLoadingFallback />}><AdminSubmissions /></Suspense>} />
                <Route path="/admin/assessathon/audit-log" element={<Suspense fallback={<RouteLoadingFallback />}><AdminAuditLog /></Suspense>} />
                <Route path="/admin/assessathon/monitor" element={<Suspense fallback={<RouteLoadingFallback />}><AdminMonitor /></Suspense>} />
                <Route path="/admin/assessathon/analytics" element={<Suspense fallback={<RouteLoadingFallback />}><AdminAnalytics /></Suspense>} />
                */}

                {/* General Platform Admin Portal (NOT Assess-A-Thon — stays live) */}
                <Route path="/adminportal" element={<AdminPortalRedirect />} />
                <Route path="/adminportal/*" element={<AdminPortalRedirect />} />
                {/* Protected Routes */}
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/policies" element={<Navigate to="/policies/terms-and-conditions" replace />} />
                <Route path="/policies/:policyId" element={<PoliciesPage />} />
                <Route path="/privacypolicy" element={<Navigate to="/policies/terms-and-conditions" replace />} />

                {/* Company & legal entry points (issue #263). Apple Developer
                    enrollment checks that /privacy and /terms resolve, and these
                    are the addresses people type by hand.

                    /privacy and /terms REDIRECT into the existing policies page
                    rather than getting their own copy of the text. There is one
                    canonical set of legal wording in PrivacyPolicy.js; forking it
                    would mean two versions that silently drift, and the wrong one
                    would eventually be the one someone reads. Note /privacypolicy
                    above lands on terms-and-conditions — that is pre-existing and
                    left alone, but /privacy points at the actual privacy policy,
                    which is what the word means. */}
                <Route path="/privacy" element={<Navigate to="/policies/privacy-policy" replace />} />
                <Route path="/terms" element={<Navigate to="/policies/terms-and-conditions" replace />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/sitemap" element={<SitemapPage />} />
                <Route path="/generator" element={
                  <ProtectedRoute>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <GeneratorPage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/generator/:lessonId" element={
                  <ProtectedRoute>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <GeneratorPage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/settings" element={
                  <ProtectedRoute>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <SettingsPage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/fellowship" element={
                  <ProtectedRoute>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <FellowDashboard />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/fellowship/manage" element={
                  <ProtectedRoute>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <FellowManage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="/fellowship/store" element={
                  <ProtectedRoute>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <RedemptionStore />
                    </Suspense>
                  </ProtectedRoute>
                } />
                {/* Live Quiz — public, no auth */}
                <Route path="/quiz/:sessionId" element={<QuizJoinPage />} />

                {/* Catch-all route for unknown paths */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              <HideOnRoutes paths={['/freshmen2026', '/freshman2026']}>
                <FloatingThemeToggle />
              </HideOnRoutes>
              {/* Outside <Routes> on purpose — consent has to be collected on
                  every page, not per-route (issue #264). It renders nothing
                  once a choice is stored, so this costs a localStorage read. */}
              <CookieConsent />
            </Router>
          </ThemeProvider>
        </AuthProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
